import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { CloseActionMethod } from "@redotech/redo-model/team";

export type CloseNavigationTarget =
  | { kind: "stay" }
  | { kind: "table" }
  | { kind: "next"; nextSupportTicketId: SupportTicketId };

/**
 * Pure helper that picks the post-close navigation target from the merchant's
 * "After closing ticket action" setting. Falls back to stay when the setting
 * is missing — non-close edits should never reroute the user, and an undefined
 * close setting should not surprise them.
 *
 * For NEXT, prefers the row after the closed ticket; otherwise falls back to
 * the row before, mirroring the previous auto-advance behavior. With no
 * adjacent row, it routes to the table.
 */
export function getCloseNavigationTarget({
  closeAction,
  data,
  supportTicketId,
}: {
  closeAction: CloseActionMethod | null | undefined;
  data: readonly SupportTicketListItemResponse[];
  supportTicketId: SupportTicketId;
}): CloseNavigationTarget {
  if (closeAction === CloseActionMethod.TABLE) {
    return { kind: "table" };
  }
  if (closeAction !== CloseActionMethod.NEXT) {
    return { kind: "stay" };
  }
  const index = data.findIndex((t) => t.id === supportTicketId);
  if (index !== -1 && index < data.length - 1) {
    return { kind: "next", nextSupportTicketId: data[index + 1]!.id };
  }
  if (index > 0) {
    return { kind: "next", nextSupportTicketId: data[index - 1]!.id };
  }
  return { kind: "table" };
}
